import { useMemo, useState } from 'react'
import { formatAmount, money } from '../../../shared/stash'
import { iconFor, stash, toCents, type Pack, type SaleResult } from '../../lib/stash'
import { smallKick } from '../../lib/fun'
import Sheet from './Sheet'
import Icon from '../Icon'

/**
 * What is ready to sell, stacked: "Blue Dream · 3.5 g · $40 × 8".
 * One tap on SELL sells the top one of the stack.
 */
export default function Shelf({ packs, onSold, onGo }: { packs: Pack[]; onSold: (r: SaleResult) => void; onGo: () => void }) {
  const [selling, setSelling] = useState<Pack[] | null>(null)

  const stacks = useMemo(() => {
    const m = new Map<string, Pack[]>()
    for (const p of packs) {
      const k = `${p.batch_id}|${p.label}|${p.amount_milli}|${p.price_cents}`
      m.set(k, [...(m.get(k) ?? []), p])
    }
    return [...m.values()]
  }, [packs])

  if (!packs.length) {
    return (
      <div className="empty">
        <div className="empty-icon"><Icon name="shop" size={44} /></div>
        <h2>Shelf's empty</h2>
        <p className="muted">Log a batch and bag it up — packages show up here, ready to sell.</p>
        <button type="button" className="cta" onClick={onGo}><Icon name="box" /> Go to my stash</button>
      </div>
    )
  }

  return (
    <div className="shelf">
      <h1 className="page-title">Sell <small>{packs.length} ready</small></h1>
      {stacks.map((stack) => {
        const p = stack[0]!
        return (
          <div key={p.id} className="sell-card">
            <div className="sell-head">
              <span className="sell-icon"><Icon name={iconFor(p.category)} size={34} /></span>
              <div className="stack-body">
                <b>{p.label}</b>
                <span>{formatAmount(p.amount_milli, p.unit)}</span>
              </div>
              <span className="sell-left"><b>{stack.length}</b>left</span>
            </div>
            <button type="button" className="sell-btn" onClick={(e) => { smallKick(e); setSelling(stack) }}>
              <Icon name="cash" size={26} /> SELL · {money(p.price_cents)}
            </button>
          </div>
        )
      })}
      {selling && <SellSheet stack={selling} onClose={() => setSelling(null)} onSold={(r) => { setSelling(null); onSold(r) }} />}
    </div>
  )
}

function SellSheet({ stack, onClose, onSold }: { stack: Pack[]; onClose: () => void; onSold: (r: SaleResult) => void }) {
  const p = stack[0]!
  const [price, setPrice] = useState((p.price_cents / 100).toString())
  const [note, setNote] = useState('')
  const [qty, setQty] = useState(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cents = toCents(price)
  const n = Math.min(Math.max(1, qty), stack.length)
  const profit = cents !== null ? (cents - p.cost_cents) * n : null

  async function sell() {
    if (cents === null) { setError('That price is not a number.'); return }
    setBusy(true)
    setError(null)
    try {
      onSold(await stash.sell(stack.slice(0, n).map((x) => x.id), { soldCents: cents, note: note || undefined }))
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <Sheet title={`Sell ${p.label}`} onClose={onClose}>
      <p className="muted">{formatAmount(p.amount_milli, p.unit)} · {stack.length} left in this stack</p>
      <div className="qty">
        <span className="qty-label">How many?</span>
        <div className="qty-row">
          <button type="button" className="qty-btn" aria-label="One less" disabled={n <= 1} onClick={() => setQty(n - 1)}>−</button>
          <b className="qty-num" aria-live="polite">{n}</b>
          <button type="button" className="qty-btn" aria-label="One more" disabled={n >= stack.length} onClick={() => setQty(n + 1)}>+</button>
        </div>
        {stack.length > 1 && (
          <div className="chips qty-chips">
            {[...new Set([1, 2, 3, 5, stack.length])].filter((x) => x <= stack.length).map((x) => (
              <button key={x} type="button" className={`chip ${n === x ? 'on' : ''}`} onClick={() => setQty(x)}>
                {x === stack.length && x > 1 ? `All ${x}` : x}
              </button>
            ))}
          </div>
        )}
      </div>
      <label>
        Price each
        <div className="money-input huge"><span>$</span><input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
      </label>
      {profit !== null && (
        <p className={`profit-line ${profit >= 0 ? 'up' : 'down'}`}>
          Your cost {money(p.cost_cents * n)} → profit <b>{money(profit)}</b>
        </p>
      )}
      <label>
        Note <span className="muted small">(optional — who, where)</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="e.g. Mike, downtown" />
      </label>
      {error && <p className="error">{error}</p>}
      <button type="button" className="cta big" disabled={busy || cents === null} onClick={sell}>
        <Icon name="cash" /> {busy ? 'Ringing it up…' : `Sold ${n > 1 ? `${n} for ` : 'for '}${cents !== null ? money(cents * n) : '—'}`}
      </button>
    </Sheet>
  )
}
